import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Heading, Modal, Notification } from "@telia/teddy";
import { DocumentPrerequisiteStatusEnum } from "../../../../../models/external/ocmInterface";
import { Agent } from "../../../../../models/state";

interface CollisionOrder {
    creationDate?: number;
    orderId: number;
    salesAgent?: string;
    state?: DocumentPrerequisiteStatusEnum;
}

interface OrderCollisionModalProps {
    collisionOrder: CollisionOrder | null;
    errorMessage?: string;
    currentOrderCreationDate?: number;
    currentOrderId: string;
    currentOrderSalesAgentName: string;
    salesAgents: Agent[];
    onCancelConflictingOrder: () => Promise<void>;
    onClose: () => void;
}

const formatCollisionCreationDate = (creationDate?: number): string => {
    if (!creationDate) {
        return "Ukjent";
    }

    const date = new Date(creationDate);
    if (Number.isNaN(date.getTime())) {
        return "Ukjent";
    }

    return new Intl.DateTimeFormat("nb-NO", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
};

const mapCollisionStatus = (state?: string): string => {
    if (state === "WAITING") {
        return "Venter på signering";
    }

    if (state === "SOLVED") {
        return "Ferdig";
    }

    return "Ukjent";
};

const getCollisionStatusBadgeVariant = (state?: string): "information" | "success" | "neutral" => {
    if (state === "WAITING") {
        return "information";
    }

    if (state === "SOLVED") {
        return "success";
    }

    return "neutral";
};

const OrderCollisionModal = ({
    collisionOrder,
    errorMessage,
    currentOrderCreationDate,
    currentOrderSalesAgentName,
    salesAgents,
    onCancelConflictingOrder,
    onClose,
}: OrderCollisionModalProps) => {
    const [collisionModalError, setCollisionModalError] = useState<string | null>(null);
    const [dismissedCollisionOrderId, setDismissedCollisionOrderId] = useState<number | null>(null);
    const [isCancellingConflictingOrder, setIsCancellingConflictingOrder] = useState(false);

    const collisionSalesAgentName = useMemo(() => {
        if (!collisionOrder) return "Ukjent";
        const rawSalesAgent = collisionOrder.salesAgent?.trim();

        if (!rawSalesAgent) {
            return "Ukjent";
        }

        const matchedAgent = salesAgents.find((agent) => `${agent.id}` === rawSalesAgent);
        if (matchedAgent) {
            return `${matchedAgent.firstName} ${matchedAgent.lastName}`.trim();
        }

        return /^\d+$/.test(rawSalesAgent) ? "Ukjent" : rawSalesAgent;
    }, [collisionOrder?.salesAgent, salesAgents]);

    useEffect(() => {
        if (!collisionOrder) {
            setCollisionModalError(null);
            setDismissedCollisionOrderId(null);
        }
    }, [collisionOrder]);

    if (!collisionOrder || collisionOrder.orderId === dismissedCollisionOrderId) {
        return null;
    }

    const handleClose = () => {
        setDismissedCollisionOrderId(collisionOrder.orderId);
        onClose();
    };

    const handleCancel = async () => {
        setCollisionModalError(null);
        setIsCancellingConflictingOrder(true);
        try {
            await onCancelConflictingOrder();
            setDismissedCollisionOrderId(collisionOrder.orderId);
            window.location.reload();
        } catch (e) {
            setCollisionModalError(e instanceof Error ? e.message : "Kunne ikke slette den konflikterende ordren");
        } finally {
            setIsCancellingConflictingOrder(false);
        }
    };

    const collisionStatusState = collisionOrder.state;
    const collisionStatus = mapCollisionStatus(collisionStatusState);
    const isSimCardCollision = errorMessage?.includes("SIMCARD_NOT_AVAILABLE");

    return (
        <Modal open onOpenChange={handleClose}>
            <Modal.Overlay>
                <Modal.Content>
                    <Modal.Group variant="title">
                        <Modal.Title as="h3">Validering feilet på grunn av ordrekonflikt</Modal.Title>
                        <Modal.Close aria-label="lukk" slot="floating" onClick={handleClose} />
                    </Modal.Group>

                    <Notification type="tile" variant="information" mt="100" mb="100">
                        <Notification.Icon />
                        <Notification.Text>
                            {isSimCardCollision
                                ? "Det finnes en annen ordre som inneholder samme SIM-nummer. Du kan enten gå tilbake og endre til et annet SIM-nummer på denne ordren, eller slette/kansellere den konflikterende ordren."
                                : "Det finnes en annen ordre som inneholder samme mobilnummer. Om du ønsker å fortsette med denne ordren så må den andre slettes/kanselleres."}
                        </Notification.Text>
                    </Notification>

                    <div className="retailer-summary-collision-modal__cards">
                        <div className="retailer-summary-collision-modal__card">
                            <Heading as="h4" variant="title-100" mb="100">
                                Konflikterende ordre
                            </Heading>
                            <div className="retailer-summary-collision-modal__row">
                                <span>Status</span>
                                <Badge variant={getCollisionStatusBadgeVariant(collisionStatusState)} hideIcon>
                                    {collisionStatus}
                                </Badge>
                            </div>

                            <div className="retailer-summary-collision-modal__row">
                                <span>Opprettet</span>
                                <strong>{formatCollisionCreationDate(collisionOrder.creationDate)}</strong>
                            </div>
                            <div className="retailer-summary-collision-modal__row">
                                <span>Salgsagent</span>
                                <strong>{collisionSalesAgentName}</strong>
                            </div>
                            <div className="retailer-summary-collision-modal__row">
                                <span>Ordre ID</span>
                                <strong>{collisionOrder.orderId}</strong>
                            </div>
                        </div>

                        <div className="retailer-summary-collision-modal__card">
                            <Heading as="h4" variant="title-100" mb="100">
                                Denne ordren
                            </Heading>
                            <div className="retailer-summary-collision-modal__row">
                                <span>Status</span>
                                <Badge variant="special" hideIcon>
                                    Påbegynt
                                </Badge>
                            </div>
                            <div className="retailer-summary-collision-modal__row">
                                <span>Opprettet</span>
                                <strong>{formatCollisionCreationDate(currentOrderCreationDate)}</strong>
                            </div>
                            <div className="retailer-summary-collision-modal__row">
                                <span>Salgsagent</span>
                                <strong>{currentOrderSalesAgentName}</strong>
                            </div>
                        </div>
                    </div>

                    {collisionModalError && (
                        <Notification type="tile" variant="error" mt="100">
                            <Notification.Icon />
                            <Notification.Text>{collisionModalError}</Notification.Text>
                        </Notification>
                    )}

                    <Modal.Group variant="action">
                        <Modal.Close slot="content">
                            <Button variant="secondary" onClick={handleClose} disabled={isCancellingConflictingOrder}>
                                Avbryt
                            </Button>
                        </Modal.Close>
                        <Button
                            variant="destructive"
                            loading={isCancellingConflictingOrder}
                            disabled={isCancellingConflictingOrder}
                            onClick={handleCancel}
                        >
                            Slett den konflikterende ordren
                        </Button>
                    </Modal.Group>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

export default OrderCollisionModal;
